import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import { CheckCircle2, AlertTriangle, UserPlus, Users, User, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * CustomerMatchCandidatesDialog — klantmatching UI voor Fase 3B.
 *
 * REGEL 2: alle beslissingen komen van de server. Deze UI is een dumb view
 * die vier server-response types rendert:
 *   - auto_linked  → tonen + read-only
 *   - single_strong → één kandidaat, gebruiker kan bevestigen
 *   - multi_review → meerdere kandidaten, gebruiker kiest
 *   - none         → geen match, gebruiker kan create-from-quote
 *
 * REGEL 1: alle labels NL — geen tenant-specifieke tokens.
 */
export default function CustomerMatchCandidatesDialog({
  open,
  onClose,
  quoteId,
  onLinked,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {kind, customer_id, candidates[], used_signals[], auto_link_reason, ambiguous_reason}
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    email: "",
    phone: "",
    whatsapp: "",
    city: "",
    country: "",
    force_create: false,
  });
  const [createResult, setCreateResult] = useState(null); // dedup-response

  const load = useCallback(async () => {
    if (!quoteId || !open) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/quotes/${quoteId}/candidates`);
      setResult(r.data || null);
      // Pre-fill create-form vanuit quote wanneer geen match
      if (r.data?.kind === "none" && r.data?.quote_snapshot) {
        const q = r.data.quote_snapshot;
        setCreateForm((prev) => ({
          ...prev,
          first_name: q.first_name || "",
          last_name: q.last_name || (q.name?.split(" ").slice(-1)?.[0] || ""),
          email: q.email || "",
          phone: q.phone || "",
          whatsapp: q.whatsapp || "",
          company_name: q.company_name || "",
        }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId, open]);

  useEffect(() => {
    if (open) {
      setResult(null);
      setCreating(false);
      setCreateResult(null);
      load();
    }
  }, [open, load]);

  const handleAssign = async (customerId) => {
    setSaving(true);
    try {
      await adminApi.post(`/admin/quotes/${quoteId}/assign-customer`, {
        customer_id: customerId,
      });
      toast.success("Klant gekoppeld aan offerte");
      onLinked?.(customerId);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setCreateResult(null);
    try {
      const r = await adminApi.post(
        `/admin/quotes/${quoteId}/create-customer-from-quote`,
        createForm,
      );
      const data = r.data || {};
      setCreateResult(data);
      if (data.created) {
        toast.success("Nieuwe klant aangemaakt en gekoppeld");
        onLinked?.(data.customer_id);
        onClose?.();
      } else if (data.probable_duplicate) {
        toast.warning(
          "Mogelijke dubbele klant gedetecteerd — controleer voordat u doorgaat",
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="customer-match-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-600" />
            Klantmatching offerteaanvraag
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-slate-500" data-testid="match-loading">
            Kandidaten worden opgehaald…
          </div>
        )}

        {!loading && result && (
          <div className="space-y-4">
            <MatchKindBanner kind={result.kind} reason={result.auto_link_reason} />

            {result.used_signals?.length > 0 && (
              <div className="text-xs text-slate-500" data-testid="match-signals">
                Gebruikte signalen:{" "}
                {result.used_signals.map((s) => (
                  <Badge
                    key={s}
                    variant="outline"
                    className="mr-1 bg-slate-50"
                    data-testid={`match-signal-${s}`}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            )}

            {result.kind === "auto_linked" && result.candidates?.[0] && (
              <CandidateCard
                candidate={result.candidates[0]}
                autoLinked
                testId="candidate-auto-linked"
              />
            )}

            {(result.kind === "single_strong" || result.kind === "multi_review") &&
              (result.candidates || []).map((c, idx) => (
                <CandidateCard
                  key={c.customer_id || c.id || idx}
                  candidate={c}
                  onAssign={() => handleAssign(c.customer_id || c.id)}
                  saving={saving}
                  testId={`candidate-${idx}`}
                />
              ))}

            {result.kind === "none" && !creating && (
              <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center">
                <p className="text-sm text-slate-600 mb-4">
                  Geen bestaande klant gevonden op basis van de offertegegevens.
                </p>
                <Button
                  onClick={() => setCreating(true)}
                  data-testid="btn-open-create-customer"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Nieuwe klant aanmaken vanuit offerte
                </Button>
              </div>
            )}

            {result.kind === "none" && creating && (
              <CreateCustomerForm
                form={createForm}
                setForm={setCreateForm}
                onCreate={handleCreate}
                onCancel={() => setCreating(false)}
                saving={saving}
                createResult={createResult}
                onAssignExisting={handleAssign}
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onClose?.()}
            data-testid="btn-match-close"
          >
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchKindBanner({ kind, reason }) {
  if (kind === "auto_linked") {
    return (
      <Alert className="border-green-300 bg-green-50" data-testid="match-banner-auto">
        <CheckCircle2 className="h-4 w-4 text-green-700" />
        <AlertDescription className="text-green-900 text-sm">
          <strong>Automatisch gekoppeld.</strong>{" "}
          De klant is eenduidig gematched — geen verdere actie nodig.
          {reason ? ` (${reason})` : ""}
        </AlertDescription>
      </Alert>
    );
  }
  if (kind === "single_strong") {
    return (
      <Alert className="border-blue-300 bg-blue-50" data-testid="match-banner-single">
        <Info className="h-4 w-4 text-blue-700" />
        <AlertDescription className="text-blue-900 text-sm">
          <strong>Één sterke match gevonden.</strong>{" "}
          Bevestig dat dit de juiste klant is, of maak eventueel een nieuwe aan.
        </AlertDescription>
      </Alert>
    );
  }
  if (kind === "multi_review") {
    return (
      <Alert className="border-amber-300 bg-amber-50" data-testid="match-banner-multi">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-amber-900 text-sm">
          <strong>Meerdere mogelijke klanten.</strong>{" "}
          Kies de juiste klant of maak een nieuwe aan wanneer geen van deze past.
        </AlertDescription>
      </Alert>
    );
  }
  if (kind === "none") {
    return (
      <Alert className="border-slate-300 bg-slate-50" data-testid="match-banner-none">
        <Info className="h-4 w-4 text-slate-700" />
        <AlertDescription className="text-slate-800 text-sm">
          <strong>Geen match gevonden.</strong>{" "}
          Er kan een nieuwe klant worden aangemaakt op basis van de offertegegevens.
        </AlertDescription>
      </Alert>
    );
  }
  return null;
}

function CandidateCard({ candidate, autoLinked, onAssign, saving, testId }) {
  const c = candidate || {};
  const reasons = c.reasons || [];
  const displayName =
    c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Klant";
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-2"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <User className="h-5 w-5 text-slate-500 mt-1" />
          <div>
            <div className="font-semibold text-slate-900" data-testid={`${testId}-name`}>
              {displayName}
            </div>
            {c.company_name && (
              <div className="text-xs text-slate-500">{c.company_name}</div>
            )}
            <div className="mt-1 text-sm text-slate-700 space-y-0.5">
              {c.email && <div data-testid={`${testId}-email`}>{c.email}</div>}
              {c.phone && <div data-testid={`${testId}-phone`}>{c.phone}</div>}
              {c.whatsapp && c.whatsapp !== c.phone && (
                <div data-testid={`${testId}-whatsapp`}>WhatsApp: {c.whatsapp}</div>
              )}
              {c.city && (
                <div className="text-xs text-slate-500">
                  {c.city}
                  {c.country ? `, ${c.country}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>
        {!autoLinked && onAssign && (
          <Button
            size="sm"
            onClick={onAssign}
            disabled={saving}
            data-testid={`${testId}-assign-btn`}
          >
            {saving ? "Bezig…" : "Deze klant koppelen"}
          </Button>
        )}
        {autoLinked && (
          <Badge className="bg-green-100 text-green-800 border-green-300">
            Automatisch gekoppeld
          </Badge>
        )}
      </div>
      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {reasons.map((r, i) => (
            <Badge
              key={i}
              variant="outline"
              className="text-xs bg-slate-50 text-slate-600"
            >
              {r.label || r.key}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateCustomerForm({
  form,
  setForm,
  onCreate,
  onCancel,
  saving,
  createResult,
  onAssignExisting,
}) {
  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
      data-testid="create-customer-form"
    >
      <div className="text-sm font-semibold text-slate-900">
        Nieuwe klant aanmaken op basis van offerte
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Voornaam" required>
          <Input
            value={form.first_name}
            onChange={(e) => update("first_name", e.target.value)}
            data-testid="create-customer-first-name"
          />
        </Field>
        <Field label="Achternaam" required>
          <Input
            value={form.last_name}
            onChange={(e) => update("last_name", e.target.value)}
            data-testid="create-customer-last-name"
          />
        </Field>
        <Field label="E-mail" required>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            data-testid="create-customer-email"
          />
        </Field>
        <Field label="Telefoon">
          <Input
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            data-testid="create-customer-phone"
          />
        </Field>
        <Field label="WhatsApp (indien afwijkend)">
          <Input
            value={form.whatsapp}
            onChange={(e) => update("whatsapp", e.target.value)}
            data-testid="create-customer-whatsapp"
          />
        </Field>
        <Field label="Bedrijfsnaam (optioneel)">
          <Input
            value={form.company_name}
            onChange={(e) => update("company_name", e.target.value)}
            data-testid="create-customer-company"
          />
        </Field>
        <Field label="Woonplaats">
          <Input
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            data-testid="create-customer-city"
          />
        </Field>
        <Field label="Land">
          <Input
            value={form.country}
            onChange={(e) => update("country", e.target.value)}
            data-testid="create-customer-country"
          />
        </Field>
      </div>

      {createResult?.probable_duplicate && (
        <Alert
          className="border-amber-300 bg-amber-50"
          data-testid="create-customer-duplicate-alert"
        >
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-900 text-sm">
            <strong>Mogelijke dubbele klant gedetecteerd.</strong> Één of meer bestaande
            klanten hebben overeenkomende e-mail/telefoon. Koppel liever een bestaande
            klant of zet &quot;Toch aanmaken&quot; aan indien u zeker weet dat dit een nieuwe klant is.
            <div className="mt-2 space-y-1">
              {(createResult.candidates || []).map((c, idx) => (
                <div
                  key={c.customer_id || c.id || idx}
                  className="flex items-center justify-between gap-2 rounded bg-white border border-amber-200 p-2"
                  data-testid={`create-dup-candidate-${idx}`}
                >
                  <span className="text-sm">
                    {c.display_name || `${c.first_name || ""} ${c.last_name || ""}`}{" "}
                    <span className="text-slate-500">— {c.email}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAssignExisting(c.customer_id || c.id)}
                    data-testid={`create-dup-candidate-${idx}-assign`}
                  >
                    Deze klant koppelen
                  </Button>
                </div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="force-create"
          checked={form.force_create}
          onChange={(e) => update("force_create", e.target.checked)}
          data-testid="create-customer-force"
        />
        <Label htmlFor="force-create" className="text-sm cursor-pointer">
          Toch aanmaken (dedup-check overslaan)
        </Label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={saving}
          data-testid="create-customer-cancel"
        >
          Annuleren
        </Button>
        <Button
          onClick={onCreate}
          disabled={saving || !form.first_name || !form.last_name || !form.email}
          data-testid="create-customer-submit"
        >
          {saving ? "Bezig…" : "Klant aanmaken en koppelen"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
